import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db, currentProject } from './index';
import { COLLECTIONS } from './schema';

const SETTINGS_DOC_ID = 'app';

/**
 * Get company settings from Firestore.
 * Merges with static config as fallback.
 * Returns default settings if no settings doc exists yet.
 */
export const getCompanySettings = async () => {
  try {
    const snap = await getDoc(doc(db, COLLECTIONS.settings, SETTINGS_DOC_ID));
    if (snap.exists()) {
      return { ...snap.data(), exists: true };
    }
    // Return default settings from static config
    return {
      exists: false,
      companyName: currentProject.companyName || 'Field Sync',
      companySlug: currentProject.companySlug || 'default',
      zohoOrgId: currentProject.zohoOrgId || null,
      zohoClientId: currentProject.zohoClientId || null,
      zohoDatacenter: currentProject.zohoDatacenter || 'eu',
      features: {
        clockInOutEnabled: true,
        locationTrackingEnabled: false,
        zohoIntegrationEnabled: !!currentProject.zohoClientId && currentProject.zohoClientId !== 'YOUR_ZOHO_CLIENT_ID',
        multiDayJobsEnabled: true,
        clientSignatureEnabled: true,
      },
      defaultJobStartTime: '08:00',
      defaultWorkHours: 8,
    };
  } catch (e) {
    console.error('getCompanySettings error:', e);
    return null;
  }
};

/**
 * Create or update company settings.
 * Only admins should call this.
 */
export const updateCompanySettings = async (settings, adminUid) => {
  try {
    const ref = doc(db, COLLECTIONS.settings, SETTINGS_DOC_ID);
    await setDoc(ref, {
      ...settings,
      updatedAt: serverTimestamp(),
      updatedBy: adminUid,
    }, { merge: true });
    return { error: null };
  } catch (e) {
    console.error('updateCompanySettings error:', e);
    return { error: e.message };
  }
};

/**
 * Get Zoho credentials from settings (not static config).
 * This allows per-company Zoho org IDs.
 */
export const getZohoSettings = async () => {
  const settings = await getCompanySettings();
  if (!settings) return null;
  
  return {
    zohoOrgId: settings.zohoOrgId || currentProject.zohoOrgId || null,
    zohoClientId: settings.zohoClientId || currentProject.zohoClientId || null,
    zohoDatacenter: settings.zohoDatacenter || currentProject.zohoDatacenter || 'eu',
    zohoRedirectUri: currentProject.zohoRedirectUri || `${window.location.origin}/zoho-callback`,
    zohoScopes: currentProject.zohoScopes || 'ZohoBooks.contacts.READ,ZohoBooks.estimates.READ',
    zohoToken: settings.zohoToken || currentProject.zohoToken || null,
    zohoFunctionsRegion: currentProject.zohoFunctionsRegion || 'us-central1',
    zohoIntegrationEnabled: settings.features?.zohoIntegrationEnabled ?? true,
  };
};

/**
 * Check if a feature is enabled for this company.
 */
export const isFeatureEnabled = async (featureName) => {
  const settings = await getCompanySettings();
  if (!settings || !settings.features) return true; // Default: enabled
  return settings.features[featureName] ?? true;
};

/**
 * Initialize default settings doc if it doesn't exist.
 * Call this once on admin dashboard load.
 */
export const initializeDefaultSettings = async (adminUid) => {
  const settings = await getCompanySettings();
  if (settings.exists) return { created: false, error: null };
  
  const defaultSettings = {
    companyName: currentProject.companyName || 'Field Sync',
    companySlug: currentProject.companySlug || 'default',
    features: {
      clockInOutEnabled: true,
      locationTrackingEnabled: false,
      zohoIntegrationEnabled: !!currentProject.zohoClientId && currentProject.zohoClientId !== 'YOUR_ZOHO_CLIENT_ID',
      multiDayJobsEnabled: true,
      clientSignatureEnabled: true,
    },
    defaultJobStartTime: '08:00',
    defaultWorkHours: 8,
  };
  
  return await updateCompanySettings(defaultSettings, adminUid);
};
